import { Head, Link, router } from "@inertiajs/react"
import { useMemo, useState } from "react"
import { AppLayout } from "@/layouts/app-layout"
import { PageHeader } from "@/hooks/use-page-header"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { EmptyState } from "@/components/empty-state"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { ArrowUpDown, TrendingUp, FolderOpen, TriangleAlert } from "lucide-react"
import { STATUS_CONFIG, projectShowPath } from "@/lib/project-configs"
import { computePotentiel, type ComputedPotentiel } from "@/lib/potentiel-utils"
import type { PotentielProjectRow, ProjectStatus } from "@/types"

type CommuneOption = { id: string; name: string }

type Props = {
  projects: PotentielProjectRow[]
  communes: CommuneOption[]
  status: string
  commune_id: string | null
  nav_key: string
}

type SortKey =
  | "name"
  | "surface"
  | "plu_zone"
  | "sdp_current"
  | "sdp_plu"
  | "sdp_increase_pct"
  | "sdp_increase"
  | "shab_plu"
  | "shab_increase_pct"
  | "emprise"
  | "floors"
  | "annee"
type SortDir = "asc" | "desc"

interface EnrichedRow {
  project: PotentielProjectRow
  computed: ComputedPotentiel
}

const STATUS_OPTIONS = ["pending", "accepted", "asset", "rejected", "archived"] as const

function PotentielPage({ projects, communes, status, commune_id, nav_key }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>("sdp_increase_pct")
  const [sortDir, setSortDir] = useState<SortDir>("desc")

  const enriched = useMemo<EnrichedRow[]>(
    () =>
      projects.map((p) => ({
        project: p,
        computed: computePotentiel(p),
      })),
    [projects],
  )

  const sorted = useMemo(() => {
    const arr = [...enriched]
    arr.sort((a, b) => {
      let va: number | string | null = null
      let vb: number | string | null = null

      switch (sortKey) {
        case "name":
          va = a.project.name
          vb = b.project.name
          break
        case "surface":
          va = a.project.parcel?.contenance ?? null
          vb = b.project.parcel?.contenance ?? null
          break
        case "plu_zone":
          va = a.project.plu_zone?.libelle ?? null
          vb = b.project.plu_zone?.libelle ?? null
          break
        case "sdp_current":
          va = a.computed.sdpCurrent
          vb = b.computed.sdpCurrent
          break
        case "sdp_plu":
          va = a.computed.sdpPlu
          vb = b.computed.sdpPlu
          break
        case "sdp_increase_pct":
          va = a.computed.sdpIncreasePct
          vb = b.computed.sdpIncreasePct
          break
        case "sdp_increase":
          va = a.computed.sdpIncrease
          vb = b.computed.sdpIncrease
          break
        case "shab_plu":
          va = a.computed.shabPlu
          vb = b.computed.shabPlu
          break
        case "shab_increase_pct":
          va = a.computed.shabIncreasePct
          vb = b.computed.shabIncreasePct
          break
        case "emprise":
          va = a.computed.emprisePluPct
          vb = b.computed.emprisePluPct
          break
        case "floors":
          va = a.computed.floorsPlu
          vb = b.computed.floorsPlu
          break
        case "annee":
          va = a.computed.anneeConstruction
          vb = b.computed.anneeConstruction
          break
      }

      if (va == null && vb == null) return 0
      if (va == null) return 1
      if (vb == null) return -1

      const cmp =
        typeof va === "string" && typeof vb === "string" ? va.localeCompare(vb, "fr") : (va as number) - (vb as number)
      return sortDir === "desc" ? -cmp : cmp
    })
    return arr
  }, [enriched, sortKey, sortDir])

  // Summary stats
  const stats = useMemo(() => {
    const pcts = enriched.map((e) => e.computed.sdpIncreasePct).filter((v): v is number => v != null)
    const absVals = enriched.map((e) => e.computed.sdpIncrease).filter((v): v is number => v != null)

    const avg = pcts.length > 0 ? Math.round(pcts.reduce((a, b) => a + b, 0) / pcts.length) : null
    const avgAbs = absVals.length > 0 ? Math.round(absVals.reduce((a, b) => a + b, 0) / absVals.length) : null

    const sortedPcts = [...pcts].sort((a, b) => a - b)
    const median =
      sortedPcts.length > 0
        ? sortedPcts.length % 2 === 0
          ? Math.round((sortedPcts[sortedPcts.length / 2 - 1] + sortedPcts[sortedPcts.length / 2]) / 2)
          : sortedPcts[Math.floor(sortedPcts.length / 2)]
        : null

    return { count: enriched.length, avg, avgAbs, median, withData: pcts.length }
  }, [enriched])

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortKey(key)
      setSortDir("desc")
    }
  }

  const navigate = (newStatus?: string, newCommuneId?: string) => {
    const params: Record<string, string> = {}
    params.status = newStatus ?? status
    if (newCommuneId) params.commune_id = newCommuneId
    if (newStatus && newStatus !== status) delete params.commune_id
    router.get("/potentiel-immobilier", params, { preserveState: true })
  }

  const SortHeader = ({ label, k, className }: { label: string; k: SortKey; className?: string }) => (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => toggleSort(k)}
        className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
      >
        {label}
        <ArrowUpDown className={`size-3 ${sortKey === k ? "text-primary" : "text-muted-foreground/40"}`} />
        {sortKey === k && <span className="text-[10px] text-primary">{sortDir === "asc" ? "↑" : "↓"}</span>}
      </button>
    </TableHead>
  )

  const fmtNum = (v: number | null) => (v != null ? v.toLocaleString("fr-FR") : "—")
  const fmtPct = (v: number | null) => {
    if (v == null) return "—"
    const sign = v >= 0 ? "+" : ""
    return `${sign}${v}%`
  }
  const fmtDelta = (v: number | null) => {
    if (v == null) return "—"
    const sign = v >= 0 ? "+" : ""
    return `${sign}${v.toLocaleString("fr-FR")} m²`
  }

  // Color intensity based on percentage
  const augColor = (pct: number | null) => {
    if (pct == null) return "text-muted-foreground"
    if (pct > 100) return "text-emerald-600"
    if (pct > 50) return "text-emerald-600/90"
    if (pct > 0) return "text-emerald-600/75"
    if (pct === 0) return "text-muted-foreground"
    return "text-red-600"
  }

  const augBg = (pct: number | null) => {
    if (pct == null) return ""
    if (pct > 100) return "bg-emerald-50/80"
    if (pct > 50) return "bg-emerald-50/60"
    if (pct > 0) return "bg-emerald-50/40"
    if (pct === 0) return ""
    return "bg-red-50/50"
  }

  // Compact current → PLU display
  const fmtRange = (cur: number | null, plu: number | null, unit: string) => {
    if (cur == null && plu == null) return null
    const c = cur != null ? `${cur}` : "—"
    const p = plu != null ? `${plu}` : "—"
    return { current: c, plu: p, unit }
  }

  return (
    <>
      <Head title="Potentiel Immobilier" />
      <PageHeader title="Potentiel Immobilier" />

      <div className="space-y-5">
        {/* Filters */}
        <div className="flex items-center gap-3">
          <Select value={status} onValueChange={(v) => navigate(v)}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>
                  {STATUS_CONFIG[s as ProjectStatus]?.label || s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={commune_id || ""} onValueChange={(v) => navigate(undefined, v)}>
            <SelectTrigger className="w-56">
              <SelectValue placeholder="Selectionnez une commune..." />
            </SelectTrigger>
            <SelectContent>
              {communes.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {!commune_id ? (
          <EmptyState
            icon={TrendingUp}
            title="Selectionnez une commune"
            description="Choisissez un statut puis une commune pour voir le potentiel immobilier des projets."
          />
        ) : sorted.length === 0 ? (
          <EmptyState
            icon={FolderOpen}
            title="Aucun projet"
            description="Aucun projet avec coordonnees dans cette commune."
          />
        ) : (
          <>
            {/* Summary stats */}
            <div className="flex items-end gap-10">
              <div>
                <p className="text-2xl font-semibold tabular-nums">{stats.count}</p>
                <p className="text-xs text-muted-foreground">
                  Projet{stats.count > 1 ? "s" : ""}
                  {stats.withData < stats.count && <span className="ml-1">({stats.withData} avec donnees)</span>}
                </p>
              </div>
              {stats.avg != null && (
                <div>
                  <p className="text-2xl font-semibold tabular-nums text-emerald-600">
                    {stats.avg > 0 ? "+" : ""}
                    {stats.avg}%
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Augmentation SDP moyenne
                    {stats.avgAbs != null && (
                      <span className="ml-1 tabular-nums">
                        ({stats.avgAbs > 0 ? "+" : ""}
                        {stats.avgAbs.toLocaleString("fr-FR")} m²)
                      </span>
                    )}
                  </p>
                </div>
              )}
              {stats.median != null && (
                <div>
                  <p className="text-2xl font-semibold tabular-nums text-emerald-600">
                    {stats.median > 0 ? "+" : ""}
                    {stats.median}%
                  </p>
                  <p className="text-xs text-muted-foreground">Augmentation SDP mediane</p>
                </div>
              )}
            </div>

            {/* Table */}
            <div className="rounded-lg border border-border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <SortHeader label="Projet" k="name" />
                    <SortHeader label="Surface" k="surface" className="text-right" />
                    <SortHeader label="Zone PLU" k="plu_zone" />
                    <SortHeader label="Emprise" k="emprise" className="text-center" />
                    <SortHeader label="Etages" k="floors" className="text-center" />
                    <SortHeader label="SDP actuel" k="sdp_current" className="text-right" />
                    <SortHeader label="SDP PLU" k="sdp_plu" className="text-right" />
                    <SortHeader label="Augmentation SDP" k="sdp_increase_pct" className="text-right" />
                    <SortHeader label="SHAB PLU" k="shab_plu" className="text-right" />
                    <SortHeader label="Aug. SHAB" k="shab_increase_pct" className="text-right" />
                    <SortHeader label="Annee" k="annee" className="text-center" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map(({ project, computed }) => {
                    const emprise = fmtRange(computed.empriseCurrentPct, computed.emprisePluPct, "%")
                    const floors = fmtRange(computed.floorsCurrent, computed.floorsPlu, "")

                    return (
                      <TableRow key={project.id} className="transition-colors hover:bg-muted/20">
                        {/* Projet */}
                        <TableCell>
                          <Link href={projectShowPath(project, nav_key)} className="font-medium text-primary hover:underline">
                            {project.name}
                          </Link>
                          {project.address && (
                            <p className="text-xs text-muted-foreground truncate max-w-48">{project.address}</p>
                          )}
                        </TableCell>

                        {/* Surface parcelle */}
                        <TableCell className="text-right">
                          {project.parcel ? (
                            <div>
                              <span className="text-sm tabular-nums">{fmtNum(project.parcel.contenance)} m²</span>
                              <p className="text-xs text-muted-foreground">{project.parcel.idu}</p>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>

                        {/* Zone PLU */}
                        <TableCell>
                          {project.plu_zone ? (
                            <div>
                              <span className="text-sm font-medium">{project.plu_zone.libelle}</span>
                              <p className="text-xs text-muted-foreground">{project.plu_zone.zoneType}</p>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>

                        {/* Emprise au sol: current → PLU */}
                        <TableCell className="text-center">
                          {emprise ? (
                            <div className="text-sm tabular-nums">
                              <span className="text-muted-foreground">{emprise.current}%</span>
                              <span className="text-muted-foreground/50 mx-0.5">→</span>
                              <span className="font-medium">{emprise.plu}%</span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>

                        {/* Etages: current → PLU */}
                        <TableCell className="text-center">
                          {floors ? (
                            <div className="inline-flex items-center gap-1 text-sm tabular-nums">
                              <span className="text-muted-foreground">{floors.current}</span>
                              <span className="text-muted-foreground/50">→</span>
                              <span className="font-medium">{floors.plu}</span>
                              {(computed.floorsCurrentEstimated || computed.floorsPluEstimated) && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="inline-flex">
                                        <TriangleAlert className="size-3 text-amber-500" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      Nombre d'etages estime a partir de la hauteur (hauteur / 3m)
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>

                        {/* SDP actuel */}
                        <TableCell className="text-right tabular-nums text-sm">
                          {fmtNum(computed.sdpCurrent)} {computed.sdpCurrent != null && "m²"}
                        </TableCell>

                        {/* SDP PLU */}
                        <TableCell className="text-right tabular-nums text-sm font-medium">
                          {fmtNum(computed.sdpPlu)} {computed.sdpPlu != null && "m²"}
                        </TableCell>

                        {/* Hero: Augmentation SDP */}
                        <TableCell className={`text-right px-4 ${augBg(computed.sdpIncreasePct)}`}>
                          {computed.sdpIncrease != null ? (
                            <div className="inline-flex items-start gap-1 justify-end">
                              <div>
                                <p
                                  className={`text-base font-semibold tabular-nums leading-tight ${augColor(computed.sdpIncreasePct)}`}
                                >
                                  {fmtDelta(computed.sdpIncrease)}
                                </p>
                                <p className={`text-xs tabular-nums ${augColor(computed.sdpIncreasePct)} opacity-70`}>
                                  {fmtPct(computed.sdpIncreasePct)}
                                </p>
                              </div>
                              {(computed.floorsCurrentEstimated || computed.floorsPluEstimated) && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="inline-flex mt-0.5">
                                        <TriangleAlert className="size-3 text-amber-500" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      Valeur basee sur un nombre d'etages estime (hauteur / 3m)
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>

                        {/* SHAB PLU */}
                        <TableCell className="text-right tabular-nums text-sm">
                          {fmtNum(computed.shabPlu)} {computed.shabPlu != null && "m²"}
                        </TableCell>

                        {/* Augmentation SHAB */}
                        <TableCell className="text-right">
                          {computed.shabIncrease != null ? (
                            <div className="inline-flex items-start gap-1 justify-end">
                              <div>
                                <p
                                  className={`text-sm font-medium tabular-nums leading-tight ${augColor(computed.shabIncreasePct)}`}
                                >
                                  {fmtDelta(computed.shabIncrease)}
                                </p>
                                <p
                                  className={`text-[11px] tabular-nums ${augColor(computed.shabIncreasePct)} opacity-70`}
                                >
                                  {fmtPct(computed.shabIncreasePct)}
                                </p>
                              </div>
                              {(computed.floorsCurrentEstimated || computed.floorsPluEstimated) && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="inline-flex mt-0.5">
                                        <TriangleAlert className="size-3 text-amber-500" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      Valeur basee sur un nombre d'etages estime (hauteur / 3m)
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>

                        {/* Annee de construction */}
                        <TableCell className="text-center text-sm tabular-nums text-muted-foreground">
                          {computed.anneeConstruction ?? "—"}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>
    </>
  )
}

PotentielPage.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>

export default PotentielPage
